import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { Button, Input, Textarea } from '../ui/index.js';
import type { Project } from '../../types/index.js';

interface ProjectFormProps {
  project?: Project;
  onClose: () => void;
}

export function ProjectForm({ project, onClose }: ProjectFormProps) {
  const qc = useQueryClient();
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [productionOffice, setProductionOffice] = useState(project?.productionOffice ?? '');
  const [lmName, setLmName] = useState(project?.locationManagerName ?? '');
  const [lmPhone, setLmPhone] = useState(project?.locationManagerPhone ?? '');
  const [lmEmail, setLmEmail] = useState(project?.locationManagerEmail ?? '');

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name, description,
        productionOffice,
        locationManagerName: lmName,
        locationManagerPhone: lmPhone,
        locationManagerEmail: lmEmail,
      };
      if (project) return api.put(`/projects/${project.id}`, payload);
      return api.post('/projects', payload);
    },
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success(project ? 'Project updated' : 'Project created');
      onClose();
    },
  });

  const handleSubmit = (e: FormEvent) => { e.preventDefault(); mutation.mutate(); };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Project name" value={name} onChange={e => setName(e.target.value)} placeholder="Downtown Feature Film" required />
      <Textarea label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief overview of this production" />

      <Input
        label="Production Office"
        value={productionOffice}
        onChange={e => setProductionOffice(e.target.value)}
        placeholder="123 Studio Blvd, Los Angeles, CA"
      />

      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Location Manager</p>
        <div className="space-y-3">
          <Input label="Name" value={lmName} onChange={e => setLmName(e.target.value)} placeholder="Jane Smith" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Phone" value={lmPhone} onChange={e => setLmPhone(e.target.value)} placeholder="+1 555-0100" type="tel" />
            <Input label="Email" value={lmEmail} onChange={e => setLmEmail(e.target.value)} placeholder="jane@studio.com" type="email" />
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" loading={mutation.isPending} className="flex-1">
          {project ? 'Save changes' : 'Create project'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
      </div>
    </form>
  );
}
